/**
 * Copy builders for the jurisdiction-coverage notices (statement, uncovered-state banner,
 * local-rule note). Pure functions over the boundary `JurisdictionCoverage` so every surface
 * words coverage identically. The notices are landlord-facing only — never part of a lease
 * document or an email sent to a tenant or applicant.
 */

import type { JurisdictionCoverage } from '@resida/api-types/lease-generation';

export type LocalOrdinanceCategory = JurisdictionCoverage['locality']['categories'][number];

export type CoverageSubject = 'lease' | 'disclosure set' | 'listing' | 'notice';

const MAILING_CITY_NOTE = 'Based on the city in the property address.';

const LOCAL_RULE_NOTE_COPY: Record<LocalOrdinanceCategory, (place: string) => string> = {
  security_deposit: (place) =>
    `${place} has its own security deposit rules, which aren't applied here. Check this amount before sending.`,
  late_fees: (place) =>
    `${place} has its own late-fee limits, which aren't applied here. Check this fee before sending.`,
  termination_notice: (place) =>
    `${place} has its own notice rules for ending a tenancy, which aren't applied here. Check the notice period before sending.`,
  required_disclosures: (place) =>
    `${place} may require disclosures that aren't included here. Check its requirements before sending.`,
};

/** Landlord-facing state name from the API; never a bare code. */
function stateLabel(state: JurisdictionCoverage['state']): string {
  const name = state.name.trim();
  return name && name.toUpperCase() !== state.code.toUpperCase() ? name : 'this state';
}

/** The quiet "which rules were applied" statement: `applied` is the emphasized first sentence. */
export function describeCoverageStatement(coverage: JurisdictionCoverage): {
  applied: string;
  detail: string | null;
} {
  const { state, locality, matchBasis } = coverage;
  const place = locality.name;
  const stateName = stateLabel(state);

  let applied = `Rules applied: federal, ${stateName}.`;
  let detail: string | null = null;

  if (locality.tier === 'MODELED' && place) {
    applied = `Rules applied: federal, ${stateName}, ${place}.`;
  } else if (locality.tier === 'RESEARCHED_NONE') {
    if (place) detail = `${place} has no local rules that change state law.`;
  } else if (matchBasis === 'UNINCORPORATED' && locality.tier !== 'KNOWN_UNMODELED') {
    const county = locality.type === 'county' ? place : (locality.county ?? place);
    if (county) detail = `Outside city limits, in ${county}. County rules aren't included.`;
  } else if (place) {
    detail = `${place}'s local rules aren't included.`;
  }

  if (matchBasis === 'MAILING_CITY') {
    detail = detail ? `${detail} ${MAILING_CITY_NOTE}` : MAILING_CITY_NOTE;
  }

  return { applied, detail };
}

/** Banner copy; null unless the state is not covered at all. */
export function describeUncoveredWarning(
  coverage: JurisdictionCoverage,
  subject: CoverageSubject,
): { title: string; body: string } | null {
  if (coverage.state.status !== 'UNCOVERED') return null;
  return {
    title: `Resida doesn't have rules for ${stateLabel(coverage.state)}`,
    body: `This ${subject} follows federal law only. Have a local attorney review it before sending.`,
  };
}

/** Field note; null unless a known local ordinance is cited for this category. */
export function localRuleNote(
  coverage: JurisdictionCoverage,
  category: LocalOrdinanceCategory,
): string | null {
  const { locality } = coverage;
  if (locality.tier !== 'KNOWN_UNMODELED') return null;
  if (!locality.categories.includes(category)) return null;
  if (!locality.name) return null;
  return LOCAL_RULE_NOTE_COPY[category](locality.name);
}
